/**
 * Partner organizations for non-3PH accounts.
 *
 * A user from a partner organization carries an ad-hoc capability grant
 * `org:<slug>`. It gates nothing on its own: the chat prompt reads it to name
 * the user's own organization. Never self-requestable (see
 * capability-metadata.ts).
 *
 * MIRRORS `backend/app/core/rbac/organizations.py`.
 */

export const ORG_CAPABILITY_PREFIX = 'org:'

export interface Organization {
  /** Display name used in people- and model-facing copy. */
  name: string
}

/** slug → organization. */
export const ORGANIZATIONS: Readonly<Record<string, Organization>> = {
  biosim: { name: 'BioSim' },
  galenband: { name: 'Galenband' },
  insignia: { name: 'Insignia Partners' },
}

/** The capability string marking membership of `slug`. */
export function orgCapability(slug: string): string {
  return `${ORG_CAPABILITY_PREFIX}${slug}`
}

/**
 * Display name of the first known `org:<slug>` capability held, or
 * `undefined`. `*` is ignored (a platform admin belongs to no partner org), as
 * are unknown slugs. Sorted so the answer is stable when more than one is held.
 */
export function orgNameFromCaps(caps: readonly string[] | Set<string>): string | undefined {
  for (const cap of [...caps].sort()) {
    if (!cap.startsWith(ORG_CAPABILITY_PREFIX)) continue
    const slug = cap.slice(ORG_CAPABILITY_PREFIX.length)
    // Own keys only — `org:constructor` must not resolve via the prototype.
    if (Object.hasOwn(ORGANIZATIONS, slug)) return ORGANIZATIONS[slug].name
  }
  return undefined
}
